import {
	createReduxStore,
	register,
	select as wpSelect,
	subscribe,
} from "@wordpress/data";
import { WeatherSelect, WeatherState } from "./type";
import { reducer } from "./reducer";
import { resolvers } from "./resolvers";
import { actions } from "./actions";
import { selectors } from "./selector";
import { fetchPrevision } from "@/services/fetch-prevision";
import {
	loadWeatherState,
	reviveDates,
	saveWeatherState,
} from "@/helpers/session-storage";
import { fetchPrayer } from "@/services/fetch-prayer";
import { fetchWarning } from "@/services/fetch-warning";
import { fetchTides } from "@/services/fetch-tide";
import { fetchObservation } from "@/services/fetch-observation";
import { fechCities } from "@/services/fetch-cities";
import { fetchForecastKuwait } from "@/services/fetch-kuwait-forecast";
import { getClosestPastDate } from "@/helpers/format-date";
import { fetcWorldCities } from "@/services/fetch-world-cities";
import { fetchWorldPrevision } from "@/services/fetch-world-prevision";
import { fetchImagery } from "@/services/fetch-imagery";
import { IImageryFilter } from "@/types/imagery-type";
import { getUserLocation } from "@/helpers/get-user-localization";
import { fetchDailyForcast } from "@/services/fetch-daily-forecast";
import { fechTimeline } from "@/services/fetch-timeline";
import { fetchMarineStation } from "@/services/fetch-marine-station";
import { fetchDailyMarine } from "@/services/fetch-daily-marine";
import { fetchMarineForecast } from "@/services/fetch-marine-forecast";

export const WEATHER_STORE = "weather/store";

const persistedState = reviveDates(loadWeatherState());

export const DEFAULT_STATE: WeatherState = {
	forecastCity: {
		location: {
			cityNameEn: "Airport",
			cityNameAr: "مطار الكويت",
			cityId: "40582",
		},
		userLocation: null,
		forecast: {
			forecastData: null,
			isLoading: false,
			error: null,
		},
		cities: {
			isLoading: true,
			cityData: [],
			error: null,
		},
		selectedDate: new Date(),
	},

	dailyMarine: {
		dayliesData: [],
		isLoading: true,
		error: null,
	},

	worldForecast: {
		selectedDate: new Date(),
		forecast: {
			isLoading: true,
			error: null,
			forecastData: [],
		},
		cities: {
			isLoading: true,
			error: null,
			cityData: [],
		},
		selectedLocation: {
			id: "",
			name: "",
		},
	},

	timeline: {
		isLoading: true,
		error: null,
		timelineData: [],
		areaSelected: null,
	},

	dailyForecast: {
		isLoading: true,
		error: null,
		dayliesData: [],
		stationIdSelected: "40550",
	},

	previsionMarine: {
		date: new Date(),
	},

	marineStation: {
		isLoading: true,
		stations: [],
		error: null,
	},

	marineLocation: {
		name_en: "Shuaiba",
		name_ar: "ميناء الشويخ",
		id: "202",
	},

	prayer: {
		isLoading: true,
		error: null,
		prayerData: [],
	},

	warning: {
		isLoading: true,
		error: null,
		warningData: [],
	},

	tide: {
		isLoading: true,
		error: null,
		tidesData: [],
	},

	observation: {
		stations: {
			isLoading: true,
			error: null,
			stationData: [],
		},
		imageryTiff: {
			imageryData: [],
			error: null,
			isLoading: true,
		},
		imageryFilter: IImageryFilter.dust,
		selectedDate: getClosestPastDate(new Date()),
	},

	forecastKuwait: {
		forecastData: null,
		isLoading: true,
		error: null,
		selectedDate: new Date(),
	},

	marineForecast: {
		error: null,
		isLoading: true,
		marineData: [],
	},

	...persistedState,
};

const store = createReduxStore(WEATHER_STORE, {
	reducer,
	actions,
	resolvers,
	selectors,
	controls: {
		FETCH_PREVISION(action: { cityId: string }) {
			return fetchPrevision(action.cityId);
		},
		FETCH_PRAYER() {
			return fetchPrayer();
		},
		FETCH_WARNING() {
			return fetchWarning();
		},
		FETCH_TIDE() {
			return fetchTides();
		},
		FETCH_OBSERVATIONS() {
			return fetchObservation();
		},
		FETCH_CITY() {
			return fechCities();
		},
		FETCH_FORECAST_KUWAIT(action: { selectedDate: Date }) {
			return fetchForecastKuwait(action.selectedDate);
		},
		FETCH_WORLD_CITIES() {
			return fetcWorldCities();
		},
		FETCH_WORLD_PREVISISION(action: { cityId: string }) {
			return fetchWorldPrevision(action.cityId);
		},
		FETCH_OBSERVATION_IMAGERY(action: { filter: IImageryFilter }) {
			return fetchImagery(action.filter);
		},
		FETCH_DAILY_FORECAST() {
			return fetchDailyForcast();
		},
		FETCH_TIMELINE() {
			return fechTimeline();
		},
		GET_USER_POSITION() {
			return getUserLocation();
		},
		FETCH_MARINE_STATION() {
			return fetchMarineStation();
		},
		FETCH_DAILY_MARINE_FORECAST() {
			return fetchDailyMarine();
		},
		FETCH_MARINE_FORECAST(action: { id: string }) {
			return fetchMarineForecast(action.id);
		},
	},
});

subscribe(() => {
	const store = wpSelect(WEATHER_STORE) as WeatherSelect;

	const forecast = store.getCityPrevisionState();
	const cities = store.getCityState();
	const location = store.getCityLocationState();
	const previsionMarine = store.getMarineDate();
	const prayer = store.getPrayerState();
	const tide = store.getTideState();
	const worldForecast = store.getWorldForecastPrevisionState();
	const worldCitiesState = store.getWorldCitiesState();
	const worldLocation = store.getWorldLocationState();

	saveWeatherState({
		previsionMarine,
		prayer,
		tide,
		forecastCity: {
			userLocation: null,
			location,
			selectedDate: new Date(),
			forecast,
			cities,
		},
		worldForecast: {
			forecast: worldForecast,
			cities: worldCitiesState,
			selectedLocation: worldLocation,
			selectedDate: new Date(),
		},
	});
});

if (!wpSelect(WEATHER_STORE)) {
	register(store);
}
